import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AncViewportService } from '../../services/anc-viewport.service';

/** Default i18n key (spec US11 / FR-090). */
export const ANC_VIEWPORT_NOTICE_MESSAGE =
    'For the best OXM experience, use a tablet, laptop or desktop with a browser width of at least 600 pixels.';

const DISMISSED_STORAGE_KEY = 'anc-viewport-notice-dismissed';

/**
 * Advisory banner for viewports below 600px (OEM-7023, US11). Never blocks the app,
 * never detects devices, and does not suggest an account or data problem. Dismissal
 * lasts for the browser session. Place it once in the app root, beside the router
 * outlet (never around it).
 */
@Component({
    selector: 'lib-anc-viewport-notice',
    standalone: true,
    imports: [TranslateModule],
    templateUrl: './anc-viewport-notice.component.html',
    styleUrls: ['./anc-viewport-notice.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncViewportNoticeComponent {
    /** i18n key of the message; defaults to the spec wording. */
    message = input<string>(ANC_VIEWPORT_NOTICE_MESSAGE);

    private readonly viewport = inject(AncViewportService);
    private readonly storage = this.getSessionStorage();
    private readonly dismissed = signal(this.readDismissed());

    readonly visible = computed(() => this.viewport.viewportClass() === 'mobile' && !this.dismissed());

    dismiss(): void {
        this.dismissed.set(true);
        try {
            this.storage?.setItem(DISMISSED_STORAGE_KEY, '1');
        } catch {
            // Storage blocked (private mode, policy): dismissal still holds for this page.
        }
    }

    private getSessionStorage(): Storage | null {
        try {
            return inject(DOCUMENT).defaultView?.sessionStorage ?? null;
        } catch {
            return null;
        }
    }

    private readDismissed(): boolean {
        try {
            return this.storage?.getItem(DISMISSED_STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    }
}
